/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Single source of truth for AI Admin theme colors shared by CommonThemes.js and report styles.
 */

export const AIADMIN_PRIMARY_COLOR = '#1A202C';
export const AIADMIN_PRIMARY_HOVER_COLOR = '#2d3748';
export const AIADMIN_TEXT_PRIMARY = '#333333';
export const AIADMIN_TEXT_SECONDARY = '#FFFFFF';

export const AIADMIN_SECONDARY_TEXT_COLOR = 'hsl(215, 14%, 45%)';
export const AIADMIN_MUTED_TEXT_COLOR = 'hsl(215, 14%, 65%)';
export const AIADMIN_ON_PRIMARY_MUTED_TEXT_COLOR = 'hsl(200, 15%, 75%)';
export const AIADMIN_ON_PRIMARY_SUBTLE_TEXT_COLOR = 'hsl(200, 15%, 65%)';
export const AIADMIN_ON_PRIMARY_BORDER_COLOR = 'hsla(0, 0%, 100%, 0.55)';

export const AIADMIN_SURFACE_WHITE_HEX_COLOR = '#ffffff';
export const AIADMIN_SURFACE_HOVER_COLOR = 'hsl(210, 17%, 95%)';
export const AIADMIN_SURFACE_HOVER_LIGHT_COLOR = '#f7fafc';

export const AIADMIN_BORDER_DEFAULT_COLOR = 'hsl(214, 20%, 88%)';
export const AIADMIN_TRENDS_BORDER_SUBTLE_COLOR = '#322c2c2e';
export const AIADMIN_TRENDS_BORDER_SUBTLE_FAINT_COLOR = 'rgba(50, 44, 44, 0.18)';

export const AIADMIN_TRENDS_FORM_SURFACE_COLOR = '#f8f6fa';
export const AIADMIN_TRENDS_MUTED_TEXT_COLOR = '#6a7070';
export const AIADMIN_TRENDS_TOGGLE_BG_COLOR = '#f0eef4';
export const AIADMIN_TRENDS_TOGGLE_HOVER_COLOR = '#e8e6ea';
export const AIADMIN_TRENDS_KPI_ACTIVE_SHADOW_COLOR = 'rgba(40, 113, 113, 0.25)';
export const AIADMIN_TRENDS_KPI_HOVER_SHADOW_COLOR = 'rgba(26, 32, 44, 0.12)';
export const AIADMIN_REPORT_PANEL_BORDER_COLOR = '#000000';

export const AIADMIN_OVERLAY_ON_PRIMARY_HOVER_COLOR = 'hsla(0, 0%, 100%, 0.1)';

export const AIADMIN_CHART_TEAL_PRIMARY_COLOR = '#287171';
export const AIADMIN_CHART_TEAL_SECONDARY_COLOR = '#359797';
export const AIADMIN_CHART_TEAL_TERTIARY_COLOR = '#4db3b3';
export const AIADMIN_CHART_TEAL_LIGHT_COLOR = '#5ebfbf';
export const AIADMIN_CHART_TEAL_DEEP_COLOR = '#3d5a5a';
export const AIADMIN_CHART_TEAL_SLATE_COLOR = '#6a9090';
export const AIADMIN_CHART_TEAL_MUTED_COLOR = '#6b8e8e';
export const AIADMIN_CHART_TEAL_DARK_COLOR = '#2d6a6a';
export const AIADMIN_CHART_DISLIKE_COLOR = '#e6005c';
export const AIADMIN_CHART_NEUTRAL_GRAY_COLOR = '#808080';
export const AIADMIN_CHART_MUTED_RED_COLOR = '#a05252';

export const AIADMIN_TRENDS_COUNT_CHART_COLORS = [
  AIADMIN_CHART_TEAL_PRIMARY_COLOR,
  AIADMIN_CHART_TEAL_SECONDARY_COLOR,
  AIADMIN_CHART_TEAL_TERTIARY_COLOR,
  AIADMIN_CHART_TEAL_LIGHT_COLOR,
  AIADMIN_PRIMARY_COLOR,
  AIADMIN_CHART_TEAL_DEEP_COLOR,
  AIADMIN_CHART_TEAL_SLATE_COLOR,
  AIADMIN_CHART_DISLIKE_COLOR,
  AIADMIN_CHART_NEUTRAL_GRAY_COLOR,
  AIADMIN_CHART_MUTED_RED_COLOR,
  AIADMIN_CHART_TEAL_MUTED_COLOR,
  AIADMIN_CHART_TEAL_DARK_COLOR
];

/** High-contrast colors for trend line series (one per core feature). */
export const AIADMIN_TREND_LINE_FEATURE_COLORS = {
  explainCount: '#2563eb',
  quizMcqCount: '#9333ea',
  summaryCount: '#ea580c',
  likeCount: '#059669',
  disLikeCount: AIADMIN_CHART_DISLIKE_COLOR,
  problemSolveCount: '#0891b2'
};

export const AIADMIN_TREND_LINE_FALLBACK_COLORS = [
  '#2563eb',
  '#9333ea',
  '#ea580c',
  '#059669',
  AIADMIN_CHART_DISLIKE_COLOR,
  '#0891b2',
  '#ca8a04',
  '#dc2626'
];

export const AIADMIN_TRENDS_FONT_FAMILY = 'Open Sans, Calibri, Tahoma, sans-serif';
